export type EventStatus = 'INACTIVE' | 'ACTIVE' | 'ENDED'
export type PollStatus = 'ACTIVE' | 'INACTIVE'
export type PollTemplate = 'CLASSIC' | 'TICKER'
export type TickerTextSize = 'SMALL' | 'MEDIUM' | 'LARGE'
export type TugStatus = 'ACTIVE' | 'INACTIVE'
export type CameraControlStatus = 'ACTIVE' | 'INACTIVE'
export type GiveawayStatus = 'UPCOMING' | 'OPEN' | 'CLOSED' | 'ENDED'

export type ImageFit = 'COVER' | 'CONTAIN' | 'FILL'

export interface ChannelBinding {
  eventId: string
  broadcasterUserId: string
  channelLogin: string
  botUserId: string
  addedAt: string
  edge: string | null
  botAccess: string | null
}

export interface BotBinding {
  eventId: string
  botUserId: string
  botLogin: string
  botDisplayName: string | null
  addedAt: string
}

export interface VoteCountsByPlatform {
  twitch: number
  youtube: number
}

export interface PollOption {
  optionId: string
  displayText: string
  command: string
  voteCount: number
  voteCountByPlatform: VoteCountsByPlatform
  textColor: string
  progressBarColor: string
  backgroundColor?: string | null
}

export interface PollOptionInput {
  displayText: string
  command: string
  textColor: string
  progressBarColor: string
  backgroundColor?: string | null
}

export interface PollSummary {
  pollId: string
  eventId: string
  name: string
  broadcasterUserIds: string[]
  question: string
  options: PollOption[]
  status: PollStatus
  dedupe: boolean
  totalVotes: number
  hasYouTubeVotes?: boolean
  youtubeVotesEnabled?: boolean
  startedAt: string | null
  stoppedAt: string | null
  createdAt: string
  callToActionText: string | null
  headingTextColor: string
  optionsTextColor: string
  callToActionTextColor: string
  headingBackground: string
  optionsBackground: string
  callToActionBackground: string
  alternatingOptionBackground: string | null
  template?: PollTemplate
  tickerLabel?: string | null
  ctaSubtext?: string | null
  tickerTextSize?: TickerTextSize
  tickerBlockWeights?: number[] | null
  questionFontSize?: number | null
  tickerLabelFontSize?: number | null
  displayTextFontSize?: number | null
  commandFontSize?: number | null
  numberFontSize?: number | null
  callToActionFontSize?: number | null
  closedTextFontSize?: number | null
  tickerHeight?: number | null
  backgroundImage: string | null
  backgroundImageFit: ImageFit | null
  cornerRadius: number
  borderWidth: number
  borderColor: string
  winnerOutlineColor: string
  fontKey?: string | null
  version: number
  closeGraceSeconds?: number
  graceEndsAt?: string | null
  youtubeCountsPurgedAt?: string | null
}

export interface TriviaLeaderboardRow {
  rank: number
  displayName: string
  correct: number
  answered: number
  isBroadcaster: boolean
  isModerator: boolean
}

export interface TriviaQuestionStat {
  triviaId: string
  question: string
  twitchCorrect: number
  twitchAnswered: number
  twitchPct: number | null
  youtubeCorrect: number
  youtubeAnswered: number
  youtubePct: number | null
  youtubeRemoved: boolean
}

export interface TriviaLeaderboard {
  eventId: string
  revealedQuestionCount: number
  includeStaff: boolean
  twitch: TriviaLeaderboardRow[]
  youtube: TriviaLeaderboardRow[]
  questions: TriviaQuestionStat[]
  computedAt: string
}

export interface TriviaOptionSummary {
  optionId: string
  displayText: string
  command: string
  voteCount: number
  voteCountByPlatform: VoteCountsByPlatform
  textColor: string
  progressBarColor: string
  isCorrect?: boolean | null
  backgroundColor?: string | null
}

export interface TriviaOptionInput {
  displayText: string
  command: string
  textColor: string
  progressBarColor: string
  isCorrect?: boolean
  backgroundColor?: string | null
}

export interface TriviaSummary {
  triviaId: string
  eventId: string
  name: string
  broadcasterUserIds: string[]
  question: string
  options: TriviaOptionSummary[]
  status: 'ACTIVE' | 'INACTIVE'
  dedupe: boolean
  youtubeVotesEnabled?: boolean
  totalVotes: number
  hasYouTubeVotes?: boolean
  hideLiveResults?: boolean
  showAnswersCounter?: boolean
  closeGraceSeconds?: number
  closedAt?: string | null
  graceEndsAt?: string | null
  revealedAt?: string | null
  youtubeCountsPurgedAt?: string | null
  answersIn?: VoteCountsByPlatform | null
  startedAt: string | null
  stoppedAt: string | null
  createdAt: string
  callToActionText: string | null
  headingTextColor: string
  optionsTextColor: string
  callToActionTextColor: string
  headingBackground: string
  optionsBackground: string
  callToActionBackground: string
  alternatingOptionBackground: string | null
  backgroundImage: string | null
  backgroundImageFit: ImageFit | null
  cornerRadius: number
  borderWidth: number
  borderColor: string
  winnerOutlineColor: string
  fontKey?: string | null
  questionFontSize?: number | null
  displayTextFontSize?: number | null
  commandFontSize?: number | null
  numberFontSize?: number | null
  callToActionFontSize?: number | null
  revealTextFontSize?: number | null
  version: number
}

export interface TugSide {
  displayText: string
  command: string
  voteCount: number
  voteCountByPlatform: VoteCountsByPlatform
  color: string
  textColor: string
  imageUrl: string | null
}

export interface TugSideInput {
  displayText: string
  command: string
  color: string
  textColor: string
  imageUrl: string | null
}

export interface TugSummary {
  tugId: string
  eventId: string
  name: string
  broadcasterUserIds: string[]
  question: string
  optionA: TugSide
  optionB: TugSide
  status: TugStatus
  dedupe: boolean
  totalVotes: number
  hasYouTubeVotes?: boolean
  youtubeVotesEnabled?: boolean
  startedAt: string | null
  stoppedAt: string | null
  createdAt: string
  questionTextColor: string
  finalizedText: string | null
  finalizedTextColor: string
  backgroundColor: string
  chatCommandBackground: string
  barHeight: number
  backgroundImage: string | null
  backgroundImageFit: ImageFit | null
  cornerRadius: number
  borderWidth: number
  borderColor: string
  fontKey?: string | null
  version: number
  graceEndsAt?: string | null
}

export interface Competitor {
  competitorId: string
  boardId: string
  name: string
  score: number
  nameTextColor: string
  scoreTextColor: string
  imageUrl: string | null
  scoreImageUrl: string | null
}

export interface CompetitorInput {
  name: string
  nameTextColor: string
  scoreTextColor: string
  imageUrl: string | null
  scoreImageUrl: string | null
}

export interface ScoreboardSummary {
  boardId: string
  eventId: string
  name: string
  createdAt: string
  title: string | null
  titleTextColor: string
  dividerColor: string
  backgroundColor: string
  backgroundImage: string | null
  backgroundImageFit: ImageFit | null
  cornerRadius: number
  borderWidth: number
  borderColor: string
  fontKey?: string | null
  competitors: Competitor[]
  version: number
}

export interface CameraCommand {
  command: string
  count: number
}

export interface CameraCommandInput {
  command: string
}

export interface CameraControlSummary {
  camControlId: string
  eventId: string
  name: string
  broadcasterUserIds: string[]
  commands: CameraCommand[]
  changeLimit: number
  cooldownMs: number
  counterMode: string
  youtubeVotesEnabled: boolean
  status: CameraControlStatus
  currentCommandIndex: number | null
  streak: number | null
  startedAt: string | null
  stoppedAt: string | null
  createdAt: string
  version: number
}

export interface GiveawayWinner {
  twitchUserId?: string | null
  login: string
  displayName: string
  rank: number
  kind: string
  disqualifiedAt?: string | null
  disqualifiedReason?: string | null
}

export interface GiveawayDisqualification {
  twitchUserId: string
  reason: string
  byUserId: string
  at: string
  promotedTwitchUserId: string | null
}

export interface GiveawayDraw {
  drawId: string
  methodVersion: string
  eligibleCount: number
  targetRound: number
  fallbackUsed: boolean
  drawnAt: string
  shortPool: boolean
  requestedWinnerCount: number
  requestedAlternateCount: number
  winners: GiveawayWinner[]
  alternates: GiveawayWinner[]
  disqualifications: GiveawayDisqualification[]
}

export interface GiveawaySummary {
  giveawayId: string
  eventId: string
  name: string
  broadcasterUserIds: string[]
  entryCommand: string
  winnerCount: number
  alternateCount: number
  excludeBroadcaster: boolean
  excludeModerators: boolean
  rulesUrl: string
  sponsorName: string
  prizeName: string
  prizeImageUrl: string | null
  brandLogoUrl: string | null
  brandText: string | null
  backgroundImage: string | null
  backgroundImageFit: string | null
  titleText: string | null
  openCtaText: string | null
  entrantsLabel: string | null
  openFooterText: string | null
  upcomingHeadline: string | null
  upcomingCtaText: string | null
  closedHeadline: string | null
  closedSubhead: string | null
  closedBody: string | null
  closedFooterText: string | null
  endedHeadline: string | null
  congratsText: string | null
  endedFooterText: string | null
  rulesLabel: string | null
  legalText: string | null
  fontKey: string
  textColor: string
  background: string
  borderColor: string
  borderWidth: number
  cornerRadius: number
  winnerOutlineColor: string
  accentBackground: string
  accentTextColor: string
  titleFontKey?: string | null
  textFontKey?: string | null
  captionFontKey?: string | null
  subtitleFontKey?: string | null
  prizeImageRadius?: number | null
  prizeImageBorderWidth?: number | null
  prizeImageBorderColor?: string | null
  prizeImageWidthPx?: number | null
  countDividerEnabled?: boolean | null
  countDividerColor?: string | null
  countDividerWidthPx?: number | null
  brandDividerEnabled?: boolean | null
  brandDividerColor?: string | null
  brandDividerWidthPx?: number | null
  status: GiveawayStatus
  entrantCount: number
  startedAt: string | null
  closedAt: string | null
  publishedAt: string | null
  createdAt: string
  version: number
  hasDraw: boolean
  winners: GiveawayWinner[]
  draw?: GiveawayDraw | null
}

export interface EventSummary {
  eventId: string
  slug: string
  name: string
  description: string | null
  status: EventStatus
  createdAt: string
  startedAt: string | null
  endedAt: string | null
}

export type EventMemberRole = 'owner' | 'editor'

export interface EventMember {
  eventId: string
  userId: string
  email: string | null
  displayName: string | null
  role: EventMemberRole
  addedBy: string
  addedAt: string
}

export interface ChatOverlaySummary {
  chatId: string
  name: string
  twitchChatChannels: string[]
  showTextShadow: boolean
  textShadow: string
  showBadges: boolean
  showChannelPills: boolean
  usernameFontSize: number
  usernameLineHeight: number
  messageFontSize: number
  messageLineHeight: number
  messageFontWeight: number
  usernameFontWeight: number
  imageScale: number
  messageColor: string
  blockedWords: string[]
  fontFamily: string
  youtubeSourceEnabled: boolean
  youtubeChatChannels?: string[]
  chatDelayMs: number
  width?: number | null
  height?: number | null
  show7tvEmotes?: boolean | null
  showBttvEmotes?: boolean | null
  showFfzEmotes?: boolean | null
  showYouTubeProfilePictures?: boolean | null
  createdAt: string
}

export interface SceneElement {
  elementKind: 'POLL' | 'TUG' | 'BOARD' | 'CHAT' | 'EMOTEWALL' | 'MERCH' | 'MERCH_TICKER' | 'CAMERA_CONTROL' | 'GIVEAWAY' | 'TRIVIA'
  elementId: string
  x: number
  y: number
  z: number
  scale: number
  visible: boolean
  w: number | null
  h: number | null
}

export interface OverlayScene {
  sceneId: string
  overlayId: string
  eventId: string
  name: string
  elements: SceneElement[]
  version: number
  createdAt: string
}

export interface LiveSceneSnapshot {
  sceneId: string
  name: string
  sourceVersion: number
  takenAt: string
  elements: SceneElement[]
}

export interface EventOverlay {
  overlayId: string
  eventId: string
  kind: string
  name: string
  width: number
  height: number
  defaultElementScale?: number
  liveSceneId: string | null
  liveScene: LiveSceneSnapshot | null
  sceneOrder: string[]
  version: number
  createdAt: string
  scenes: OverlayScene[]
}

export interface EventDetail extends EventSummary {
  bots: BotBinding[]
  channels: ChannelBinding[]
  polls: PollSummary[]
  trivias: TriviaSummary[]
  tugs: TugSummary[]
  scoreboards: ScoreboardSummary[]
  cameraControls: CameraControlSummary[]
  giveaways: GiveawaySummary[]
  members: EventMember[]
  youtubeSourceAttached?: boolean | null
  youtubeForeignSources?: EventYouTubeForeignSource[] | null
}

export interface EmoteWallSummary {
  emoteWallId: string
  name: string
  twitchChatChannels: string[]
  style: string
  emoteSize: number
  density: number
  durationSeconds: number
  showTwitchEmotes: boolean
  show7tvEmotes: boolean
  showBttvEmotes: boolean
  showFfzEmotes: boolean
  createdAt: string
}

export interface MerchVariantOption {
  id: string
  label: string
  linkUrl?: string | null
}

export interface MerchItem {
  productId: string
  title: string
  priceCents: number | null
  basePriceCents?: number | null
  imageUrl: string | null
  imageUrls?: string[] | null
  featuredImage?: string | null
  linkUrl: string | null
  defaultVariantId?: string | null
  variantOptionName?: string | null
  variantOptions?: MerchVariantOption[] | null
}

export type MerchItemInput = MerchItem

export interface MerchOverlayStyle {
  titleColor?: string | null
  titleFontKey?: string | null
  productTitleColor?: string | null
  productTitleFontKey?: string | null
  priceFontKey?: string | null
  boardBgColor?: string | null
  rowBgColor?: string | null
  tileBgColor?: string | null
  outerBgColor?: string | null
  outerCornerRadius?: number | null
  boardCornerRadius?: number | null
  outerBorderColor?: string | null
  outerBorderWidth?: number | null
  tabOutlineColor?: string | null
  tabTextColor?: string | null
  changeAnimation?: string | null
  tweenMs?: number | null
  showVariants?: boolean | null
  squarePhotos?: boolean | null
  offsiteMarkColor?: string | null
  textGlowPx?: number | null
  textGlowColor?: string | null
  textGlowArrows?: boolean | null
  outerPadX?: number | null
  boardPadX?: number | null
  shopLabelColor?: string | null
  ctaColor?: string | null
  outerPadTop?: number | null
  outerPadBottom?: number | null
  boardBgImage?: string | null
  productImageHeight?: number | null
  rowBorderColor?: string | null
  collapseXSize?: number | null
  collapseXColor?: string | null
}

export interface MerchTickerMessage {
  id: string
  label: string
  labelColor: string
  text: string
}

export interface MerchTickerStyle {
  fontKey?: string | null
  bottomBgColor?: string | null
  bottomOpacity?: number | null
  bottomPadTop?: number | null
  bottomPadBottom?: number | null
  pageBackground?: string | null
  topBgColor?: string | null
  textColor?: string | null
  borderColor?: string | null
  borderWidth?: number | null
  gapHeight?: number | null
  gapColor?: string | null
  topPadTop?: number | null
  topPadBottom?: number | null
  topOuterPad?: number | null
  bottomOuterPad?: number | null
  scrollSpeedPxS?: number | null
  changeAnimation?: string | null
  topTextSizePx?: number | null
  topLineHeightPx?: number | null
  bottomTextSizePx?: number | null
  bottomLineHeightPx?: number | null
  tweenMs?: number | null
  bottomScrollReverse?: boolean | null
  textGlowPx?: number | null
  textGlowColor?: string | null
  topSeparatorGapPx?: number | null
  bottomSeparatorGapPx?: number | null
  textGlowArrows?: boolean | null
  priceSlotMinCents?: number | null
  priceSlotMaxCents?: number | null
  pctSlotMaxPct?: number | null
}

export type MerchBoardMode = 'PRICES' | 'ORDERS'

export interface MerchOverlaySummary {
  merchOverlayId: string
  name: string
  storeTitle: string
  mode?: string | null
  displayStyle?: string | null
  extensionVisible?: boolean | null
  extensionDelayMs?: number | null
  autoReorder?: boolean | null
  extensionStartCollapsed?: boolean | null
  extensionStartWidthPct?: number | null
  extensionChannelLogin?: string | null
  extensionChannelId?: string | null
  shopMode?: boolean | null
  shopLabelText?: string | null
  ctaText?: string | null
  merchTestMode?: boolean | null
  merchTestScript?: boolean | null
  merchTestState?: number | null
  shopDomain: string | null
  items: MerchItem[]
  style?: MerchOverlayStyle | null
  messages?: MerchTickerMessage[] | null
  tickerStyle?: MerchTickerStyle | null
  createdAt: string
  updatedAt: string
}

export interface MerchBoardData {
  channel: string | null
  config: MerchOverlaySummary
  board: string
}

export type MerchMoveDirection = 'UP' | 'DOWN'

export interface MerchMoveResult {
  merchOverlayId: string
  moved: boolean
  order: string[]
}

export type MerchTestAction = 'TEST' | 'RESET'

export interface MerchTestResult {
  merchOverlayId: string
  version: number
  order: string[]
  state?: number | null
  boards?: MerchTestBoardOutcome[] | null
}

export interface MerchTestBoardOutcome {
  merchOverlayId: string
  ok: boolean
  state?: number | null
  version?: number | null
  order?: string[] | null
  error?: string | null
}

export interface MerchRepublishResult {
  shop: string
  version: number
  boardsPublished: number
  extensionAttempted: boolean
}

export interface ShopifyShop {
  shop: string
  appKey: string
  registeredAt: string | null
  lastWebhookAt: string | null
  connectionStatus?: string | null
}

export const SHOPIFY_CONNECTION_CONNECTED = 'connected'
export const SHOPIFY_CONNECTION_NEEDS_RECONNECT = 'needs_reconnect'
export const SHOPIFY_CONNECTION_PCD_NOT_APPROVED = 'pcd_not_approved'
export const SHOPIFY_CONNECTION_UNINSTALLED = 'uninstalled'

export interface ShopifyAuthUrl {
  url: string
  expiresAt: string
}

export interface ShopifyProduct {
  productId: string
  title: string
  imageUrl: string | null
  images?: Array<{
    mediaId: string | null
    url: string
    altText: string | null
    contentType: string | null
  }> | null
  priceCents: number | null
  linkUrl: string | null
  realOptionCount?: number | null
}

export interface MirroredProductAsset {
  assetId: string
  fileName: string | null
  contentType: string
  sizeBytes: number
  publicUrl: string
  createdAt: string
}

export interface MirroredShopifyProduct {
  productId: string
  title: string
  priceCents: number | null
  linkUrl: string | null
  asset: MirroredProductAsset
  assets?: MirroredProductAsset[] | null
  defaultVariantId?: string | null
  variantOptionName?: string | null
  variantOptions?: MerchVariantOption[] | null
}

export interface MerchShopStatus {
  shop: string
  appKey: string
  accountId: string
  ownerEmail?: string | null
  broadcasterId: string | null
  version: number
  lastWebhookAt: string | null
  registeredAt: string | null
  productCount: number
  webhooks: string | null
  connectionStatus?: string | null
  uninstalledAt?: string | null
  source?: string | null
}

export interface MerchApp {
  appKey: string
  label: string | null
  distribution: string | null
  ownerAccountId: string | null
  ownerEmail: string | null
  installLinkUrl: string | null
  installLinkIssuedAt: string | null
  installLinkExpiresAt: string | null
  shopCount: number
  createdAt: string | null
}

export interface AssetReference {
  elementKind: string
  elementId: string
  elementName: string
  eventId: string
  eventName: string
}

export interface AssetSummary {
  assetId: string
  fileName: string | null
  contentType: string
  sizeBytes: number
  publicUrl: string
  createdAt: string
  references: AssetReference[]
  usedByMerchBoards?: string[] | null
  replacedByAssetId?: string | null
  replacedAt?: string | null
  replacesAssetId?: string | null
}

export interface AssetReplaceResult {
  fromAssetId: string
  toAssetId: string
  repointed: number
  skippedChanged: number
  failed: number
  merchBoardsRepointed: number
  complete: boolean
}

export interface DeletionResult {
  id: string
  deleted: boolean
}

export interface OverlayLink {
  linkId: string
  k: string | null
  eventId: string
  label: string
  connectionCap: number
  revoked: boolean
  createdAt: string
  expiresAt: string | null
}

export type AccountOverlayLinkKind = 'chat' | 'emotes' | 'merch'

export interface AccountOverlayLink {
  linkId: string
  k: string
  targetKind: string
  targetId: string
  connectionCap: number
  createdAt: string
}

export interface ImageUploadUrl {
  uploadUrl: string
  publicUrl: string
  expiresAt: string
  assetId?: string
  s3Key?: string
}

export interface ProfilePictureSyncResult {
  pictureUrl: string | null
  action: string
}

export interface BotInfo {
  userId: string
  login: string
  displayName: string | null
  scopes: string[]
  connectedAt: string | null
  needsReauth: boolean
  lastRevocationReason: string | null
  lastRevocationAt: string | null
}

export interface ChannelSubscription {
  subscriptionId: string
  channelLogin: string
  broadcasterUserId: string
  botUserId: string | null
  type: string
  kind?: string | null
  status: string
  createdAt: string
  revocationReason: string | null
  revokedAt: string | null
}

export interface ReconnectChannelResult {
  channelLogin: string
  status: string
  newSubscriptionId: string | null
  error: string | null
}

export interface ReconnectBotResult {
  bot: BotInfo
  channels: ReconnectChannelResult[]
}

export interface TwitchAuthUrl {
  url: string
  state: string
  expiresAt: string
}

export interface UnsubscribeResult {
  subscriptionId: string
  deleted: boolean
}

export interface Connection {
  provider: string
  status: string
  twitchUserId: string | null
  youtubeChannelId: string | null
  login: string | null
  displayName: string | null
  avatarUrl: string | null
  scopes: string[]
  connectedAt: string | null
  botModStatus: string | null
}

export interface TwitchConnectUrl {
  url: string
  expiresAt: string
}

export interface DisconnectConnectionResult {
  provider: string
  disconnected: boolean
}

export interface YouTubeConnectUrl {
  url: string
  expiresAt: string
}

export interface YouTubeConsentResult {
  accountId: string
  youtubeConsentAt: string
}

export interface YouTubeInviteResult {
  accountId: string
  youtubeChatInvited: boolean
  previousValue: boolean
  revocation: YouTubeInviteRevocation | null
}

export interface YouTubeInviteRevocation {
  hadConnection: boolean
  disconnected: boolean
  purgeComplete: boolean
  consentCleared: boolean
}

export interface DeleteYouTubeDataResult {
  completed: boolean
  purgedEvents: number
  message: string | null
}

export interface YouTubeWaitlistResult {
  youtubeWaitlistAt: string
}

export interface YouTubeDemoExemptResult {
  accountId: string
  youtubeDemoExempt: boolean
}

export interface SetAccountSuspendedResult {
  accountId: string
  suspended: boolean
  suspendedAt: string | null
  previouslySuspended: boolean
}

export interface UsageCounterPair {
  used: number
  cap: number | null
}

export interface UsageCounters {
  votesIngested: number | null
  uploads: number | null
  uploadBytes: number | null
  republishes: number | null
  overlayTokenMints: number | null
  ticketExchanges: number | null
  ytUnits: number | null
  ytSessionsStarted: number | null
  ytSessionSeconds: number | null
  eventsCreated: number | null
  chatOverlaysCreated: number | null
  emoteWallsCreated: number | null
  merchOverlaysCreated: number | null
  pollsCreated: number | null
  tugsCreated: number | null
  boardsCreated: number | null
}

export interface AccountUsageRow {
  accountId: string
  email: string | null
  tier: string
  youtubeChatInvited: boolean
  youtubeWaitlistAt: string | null
  youtubeDemoExempt: boolean
  suspendedAt?: string | null
  createdAt?: string
  estimatedCents?: number | null
  ytQuotaPctOfMonthlyBudget?: number | null
  events?: UsageCounterPair
  chatOverlays?: UsageCounterPair
  emoteWalls?: UsageCounterPair
  storageBytes?: UsageCounterPair
  currentMonthUsage?: UsageCounters | null
  billingSource?: string | null
  cancelAtPeriodEnd?: boolean | null
  currentPeriodEnd?: string | null
}

export interface BoundEventRef {
  eventId: string
  name: string
  status: EventStatus
}

export interface AdminSubscriptionRow extends ChannelSubscription {
  boundEvents: BoundEventRef[]
  canForceUnsubscribe: boolean
}

export interface SubscriptionCounts {
  revoked: number
  pending: number
}

export interface SubscriptionPage {
  items: AdminSubscriptionRow[]
  nextToken: string | null
  counts: SubscriptionCounts
}

export interface AccountUsagePage {
  items: AccountUsageRow[]
  nextToken: string | null
  orderMode: string
  rankedAt: string | null
  approxTotal: number | null
}

export interface FleetMonth {
  month: string
  counters: UsageCounters | null
  estimatedCents: number | null
  accountCount: number
  accountsWithUsage: number
  rolledUpAt: string
}

export interface FleetCountsMonth {
  month: string
  accountCount: number
  streamCount: number | null
  rolledUpAt: string
}

export interface WaitlistEntry {
  accountId: string
  email: string | null
  youtubeWaitlistAt: string
  youtubeDemoExempt: boolean
}

export interface WaitlistPage {
  items: WaitlistEntry[]
  nextToken: string | null
  totalWaiting: number
}

export interface EventPage {
  items: Array<Pick<EventSummary, 'eventId' | 'slug' | 'name' | 'status' | 'createdAt'>>
  nextToken: string | null
}

export interface AccountUsageHistoryRow {
  month: string
  counters: UsageCounters | null
  estimatedCents: number | null
}

export interface AccountDeletionStatus {
  accountId: string
  status: string
  requestedAt: string
  completedAt: string | null
  wasSuspended: boolean
  steps: string | null
}

export interface AccountDeletionRow extends AccountDeletionStatus {
  accountExists: boolean
  email: string | null
  needsAction: boolean
}

export interface AccountDeletionPage {
  items: AccountDeletionRow[]
  nextToken: string | null
}

export interface YouTubeTelemetryDay {
  date: string
  units: number
  calls: number
  demoUnits: number
  demoCalls: number
  sessionsStarted: number
  refusedSessionStarts: number
  refusedNotLive: number
  refusedAlreadyRunning: number
  peakUnitsPerMinute: number
}

export interface YouTubeTelemetry {
  budget: number
  remainingToday: number
  days: YouTubeTelemetryDay[]
}

export interface EventYouTubeSourceResult {
  eventId: string
  youtubeSourceAttached: boolean
}

export interface YouTubeChatSession {
  sessionId: string
  eventId: string | null
  scope: string
  status: string
  startedAt: string
  stoppedAt: string | null
  stopReason: string | null
  channelId?: string | null
  channelTitle?: string | null
  videoId?: string | null
  channelProfileImageUrl?: string | null
  foreignEvent?: boolean | null
}

export interface YouTubeChatSourceResult {
  resolved: boolean
  refusalCode: string | null
  message: string | null
  channelId: string | null
  channelTitle: string | null
  channelProfileImageUrl: string | null
  videoId: string | null
  live: boolean | null
}

export interface EventYouTubeForeignSource {
  channelId: string
  videoId: string
  attestedAt: string
  channelTitle?: string | null
  channelProfileImageUrl?: string | null
}

export interface EventYouTubeForeignSourceResult {
  eventId: string
  attached: boolean
  refusalCode: string | null
  message: string | null
  source: EventYouTubeForeignSource | null
  session: YouTubeChatSession | null
}

export interface YouTubeChatSessionResult {
  started: boolean
  refusalCode: string | null
  message: string | null
  session: YouTubeChatSession | null
}

export interface PlanLimits {
  eventsPerAccount: number | null
  chatOverlaysPerAccount: number | null
  emoteWallsPerAccount: number | null
  twitchChannelsPerOverlay: number | null
  youtubeChannelsPerOverlay: number | null
  assetStorageBytes: number | null
  interactiveElementsPerStream: number | null
  voteChannelsPerElement: number | null
  interactiveContent: boolean
}

export interface PlanLimitsWithMerch extends PlanLimits {
  merchTicker?: boolean
}

export type PlanFeatureKey = 'interactiveContent'

export type PlanLimitKey = Exclude<keyof PlanLimits, PlanFeatureKey>

export interface Plan {
  tier: string
  label?: string | null
  purchasable: boolean
  limits: PlanLimits
}

export interface MyAccountUsage {
  events: number
  chatOverlays: number
  emoteWalls?: number
  storageBytes: number
}

export interface MyAccount {
  accountId: string
  tier: string
  acquisitionSource?: string
  youtubeChatInvited: boolean
  youtubeSessionsPerAccount?: number | null
  youtubeConsentAt: string | null
  youtubeWaitlistAt: string | null
  suspendedAt?: string | null
  limitsEnforced: boolean
  limits: PlanLimits
  plans: Plan[]
  usage: MyAccountUsage
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
}

export type YouTubeSpeedLevel = 'ECONOMY' | 'STANDARD' | 'FAST' | 'LIVE'

export type YouTubeEffectiveSpeedLevel = YouTubeSpeedLevel | 'SAVER'

export interface YouTubeLiveSession {
  sessionId: string | null
  accountId: string | null
  accountEmail: string | null
  startedByEmail: string | null
  channelId: string | null
  channelTitle: string | null
  scope: string | null
  eventId: string | null
  eventName: string | null
  startedAt: string | null
  lastReadAt: string | null
  unitsUsed: number | null
  level: string | null
  hot: boolean | null
  stale: boolean | null
  demoExempt: boolean | null
  foreignEvent: boolean | null
  countsTowardAppLimit: boolean | null
  countsTowardAccountLimit: boolean | null
}

export interface YouTubeSpeedPanel {
  defaultLevel: string | null
  liveAllowed: boolean | null
  brakeEnabled: boolean | null
  dailyBudgetUnits: number | null
  dailyBudgetSaved: boolean | null
  demoHoldbackSlots: number | null
  appChatLimit: number | null
  appChatLimitSaved: boolean | null
  appChatLimitMax: number | null
  accountChatLimit: number | null
  accountChatLimitSaved: boolean | null
  updatedByEmail: string | null
  updatedAt: string | null
  pacificDay: string | null
  unitsToday: number | null
  resetsAt: string | null
  brakeCapLevel: string | null
  brakeCapSince: string | null
  quotaExhaustedAt: string | null
  slotsInUse: number | null
  sessions: YouTubeLiveSession[] | null
}

export interface EventYouTubeSpeed {
  eventId: string | null
  level: string | null
  appDefaultLevel: string | null
  effectiveLevel: string | null
  liveAllowed: boolean | null
  brakeCapLevel: string | null
  updatedAt: string | null
}

export interface YouTubeStaffStopResult {
  stopped: boolean | null
  session: YouTubeChatSession | null
}
